import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserSidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Crown, Copy, Link2, Trash2, ExternalLink, Sparkles, Pencil, Power } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/utils";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [proInfo, setProInfo] = useState({ pro_status: "NONE", price: 19.99, currency: "EUR" });
  const [stripe, setStripe] = useState(null);
  const [pk, setPk] = useState(""); const [sk, setSk] = useState(""); const [wh, setWh] = useState(""); const [mode, setMode] = useState("test");
  const [links, setLinks] = useState([]);
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ amount: "", currency: "EUR", description: "" });
  const [editLink, setEditLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const isPro = proInfo.pro_status === "ACTIVE" || user?.pro_status === "ACTIVE";

  const loadAll = async () => {
    try {
      const { data: p } = await api.get("/pro/status"); setProInfo(p);
      if (p.pro_status === "ACTIVE") {
        const { data: s } = await api.get("/users/me/stripe-settings"); setStripe(s); setPk(s.publishable_key || ""); setMode(s.mode || "test");
        const { data: l } = await api.get("/payment-links"); setLinks(l);
      }
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadAll(); }, [user?.pro_status]);

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pro/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setBusy(false); }
  };

  const saveStripe = async () => {
    try { await api.put("/users/me/stripe-settings", { publishable_key: pk, secret_key: sk || undefined, webhook_secret: wh || undefined, mode }); toast.success("Stripe ključevi spremljeni"); setSk(""); setWh(""); loadAll(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const createLink = async () => {
    try { await api.post("/payment-links", { ...linkForm, amount: parseFloat(linkForm.amount) }); toast.success("Link kreiran"); setLinkDialog(false); setLinkForm({ amount: "", currency: "EUR", description: "" }); loadAll(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const deleteLink = async (id) => { try { await api.delete(`/payment-links/${id}`); toast.success("Obrisano"); loadAll(); } catch (e) { toast.error(formatError(e)); } };

  const saveEditLink = async () => {
    try {
      await api.put(`/payment-links/${editLink.id}`, { amount: parseFloat(editLink.amount), currency: editLink.currency, description: editLink.description });
      toast.success("Link ažuriran"); setEditLink(null); loadAll();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggleLink = async (l) => {
    try {
      await api.put(`/payment-links/${l.id}`, { status: l.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" });
      toast.success(l.status === "ACTIVE" ? "Link deaktiviran" : "Link aktiviran"); loadAll();
    } catch (e) { toast.error(formatError(e)); }
  };

  const shareUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/l/${id}`;

  const copyLink = (id) => {
    navigator.clipboard.writeText(shareUrl(id));
    toast.success("Link kopiran — s logom za WhatsApp");
  };

  return (
    <div className="stream-dark flex flex-col lg:flex-row min-h-screen"><UserSidebar />
      <main className="flex-1 p-6 lg:p-10 stream-scroll">
        <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white mb-6 sm:mb-8">Postavke</h1>

        {/* Pro Card */}
        {!isPro ? (
          <div className="relative overflow-hidden card-dark p-8 mb-8 max-w-3xl border-[#FFD60A]/40">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#FFD60A] rounded-full blur-[100px] opacity-20"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD60A]/10 border border-[#FFD60A]/30 mb-4">
                <Crown size={14} className="text-[#FFD60A]"/><span className="text-xs uppercase tracking-widest text-[#FFD60A]">Lifetime Pro</span>
              </div>
              <h2 className="font-heading font-black text-3xl text-white mb-3">Nadogradite na Lifetime Pro</h2>
              <p className="text-white/60 mb-4">Aktivirajte vlastite Stripe ključeve i primajte uplate direktno na svoj Stripe račun. Kreirajte shareable payment linkove za klijente.</p>
              <ul className="text-white/70 text-sm space-y-2 mb-6">
                <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#FFD60A]"/> Doživotan pristup — jednokratna uplata</li>
                <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#FFD60A]"/> Vlastiti Stripe ključevi u postavkama</li>
                <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#FFD60A]"/> Neograničeni payment linkovi za klijente</li>
                <li className="flex items-center gap-2"><Sparkles size={14} className="text-[#FFD60A]"/> Uplate idu direktno vama, ne platformi</li>
              </ul>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-heading font-black text-5xl text-[#FFD60A]">{formatMoney(proInfo.price)}</span>
                <span className="text-white/60 text-xl">{proInfo.currency}</span>
                <span className="text-white/40 text-sm ml-2">jednokratno</span>
              </div>
              <Button className="btn-yellow rounded-full px-8 h-12" onClick={upgrade} disabled={busy} data-testid="upgrade-pro">
                {busy ? "Preusmjeravamo…" : <><Crown size={16} className="mr-2"/>NADOGRADI SADA</>}
              </Button>
            </div>
          </div>
        ) : (
          <div className="card-dark p-6 mb-8 max-w-3xl border-[#FFD60A]/40">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#FFD60A] flex items-center justify-center"><Crown size={22} className="text-black"/></div>
              <div>
                <div className="font-heading font-bold text-xl text-white">Lifetime Pro Aktivan</div>
                {proInfo.activated_at && <div className="text-white/50 text-sm">Aktivirano: {new Date(proInfo.activated_at).toLocaleDateString("hr-HR")}</div>}
              </div>
            </div>
          </div>
        )}

        {/* Stripe & Links only for Pro */}
        {isPro && (
          <>
            <div className="card-dark p-8 mb-8 max-w-3xl">
              <h2 className="font-heading font-bold text-2xl text-white mb-2">Moji Stripe ključevi</h2>
              <p className="text-white/60 mb-6 text-sm">Ovi ključevi se koriste za vaše payment linkove — uplate idu direktno na vaš Stripe.</p>
              <div className="space-y-4">
                <div><Label className="text-white/80">Publishable Key</Label><Input value={pk} onChange={(e) => setPk(e.target.value)} placeholder="pk_test_… ili pk_live_…" className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="my-pk"/></div>
                <div><Label className="text-white/80">Secret Key {stripe?.secret_key_masked && <span className="text-white/40 text-xs ml-2">Trenutno: {stripe.secret_key_masked}</span>}</Label><Input type="password" value={sk} onChange={(e) => setSk(e.target.value)} placeholder="sk_test_… (prazno = ne mijenjaj)" className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="my-sk"/></div>
                <div><Label className="text-white/80">Webhook Secret {stripe?.webhook_secret_masked && <span className="text-white/40 text-xs ml-2">Trenutno: {stripe.webhook_secret_masked}</span>}</Label><Input type="password" value={wh} onChange={(e) => setWh(e.target.value)} placeholder="whsec_… (opcionalno)" className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="my-wh"/></div>
                <div><Label className="text-white/80">Mode</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-[#171717] border-white/10 text-white">
                      <SelectItem value="test">Test</SelectItem>
                      <SelectItem value="live">Live</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="btn-yellow rounded-full" onClick={saveStripe} data-testid="my-stripe-save">Spremi Stripe ključeve</Button>
              </div>
            </div>

            <div className="card-dark p-8 max-w-3xl">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="font-heading font-bold text-2xl text-white">Moji payment linkovi</h2>
                  <p className="text-white/60 text-sm mt-1">Podijelite ove linkove — svatko ih može otvoriti i platiti vama.</p>
                </div>
                <Button className="btn-yellow rounded-full" onClick={() => setLinkDialog(true)} disabled={!stripe?.configured} data-testid="new-link"><Link2 size={16} className="mr-2"/>Novi link</Button>
              </div>
              {!stripe?.configured && <p className="text-yellow-400 text-sm mb-4">Prvo unesite Stripe ključeve iznad.</p>}
              {links.length === 0 ? <div className="text-white/50 text-center py-8">Nema linkova.</div> : (
                <div className="space-y-3">
                  {links.map((l) => (
                    <div key={l.id} className="p-4 bg-white/5 rounded-xl border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`link-${l.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-[#FFD60A] font-heading font-bold text-lg">{formatMoney(l.amount)} {l.currency}</div>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${l.status === "ACTIVE" ? "bg-green-500/15 text-green-400" : "bg-white/10 text-white/50"}`} data-testid={`link-status-${l.id}`}>
                            {l.status === "ACTIVE" ? "Aktivan" : "Neaktivan"}
                          </span>
                        </div>
                        <div className="text-white/70 text-sm">{l.description}</div>
                        <div className="text-white/40 text-xs mt-1 font-mono break-all">{shareUrl(l.id)}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="ghost" className="text-white/70 hover:bg-white/10" onClick={() => copyLink(l.id)} data-testid={`copy-${l.id}`}><Copy size={14}/></Button>
                        <Button size="sm" variant="ghost" className="text-white/70 hover:bg-white/10" onClick={() => setEditLink({ ...l, amount: String(l.amount) })} data-testid={`edit-link-${l.id}`}><Pencil size={14}/></Button>
                        <Button size="sm" variant="ghost" className={l.status === "ACTIVE" ? "text-green-400 hover:bg-green-500/10" : "text-white/40 hover:bg-white/10"} onClick={() => toggleLink(l)} data-testid={`toggle-link-${l.id}`}><Power size={14}/></Button>
                        <Button size="sm" variant="ghost" className="text-white/70 hover:bg-white/10" onClick={() => window.open(`/pay/${l.id}`, "_blank")}><ExternalLink size={14}/></Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => deleteLink(l.id)} data-testid={`delete-link-${l.id}`}><Trash2 size={14}/></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
              <DialogContent className="bg-[#171717] border-white/10 text-white">
                <DialogHeader><DialogTitle>Kreiraj payment link</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-white/80">Iznos</Label><Input type="number" step="0.01" value={linkForm.amount} onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="link-amount"/></div>
                    <div><Label className="text-white/80">Valuta</Label><Input value={linkForm.currency} onChange={(e) => setLinkForm({ ...linkForm, currency: e.target.value.toUpperCase() })} className="bg-white/5 border-white/10 mt-2"/></div>
                  </div>
                  <div><Label className="text-white/80">Opis / poruka uplatitelju</Label><Textarea value={linkForm.description} onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })} placeholder="Ova poruka se prikazuje na traci na dnu linka" className="bg-white/5 border-white/10 mt-2" data-testid="link-desc"/></div>
                </div>
                <DialogFooter><Button className="btn-yellow" onClick={createLink} data-testid="link-create">Kreiraj</Button></DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={!!editLink} onOpenChange={(o) => !o && setEditLink(null)}>
              <DialogContent className="bg-[#171717] border-white/10 text-white">
                <DialogHeader><DialogTitle>Uredi payment link</DialogTitle></DialogHeader>
                {editLink && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-white/80">Iznos</Label><Input type="number" step="0.01" value={editLink.amount} onChange={(e) => setEditLink({ ...editLink, amount: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="edit-link-amount"/></div>
                      <div><Label className="text-white/80">Valuta</Label><Input value={editLink.currency} onChange={(e) => setEditLink({ ...editLink, currency: e.target.value.toUpperCase() })} className="bg-white/5 border-white/10 mt-2" data-testid="edit-link-currency"/></div>
                    </div>
                    <div><Label className="text-white/80">Opis / poruka uplatitelju</Label><Textarea value={editLink.description || ""} onChange={(e) => setEditLink({ ...editLink, description: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="edit-link-desc"/></div>
                  </div>
                )}
                <DialogFooter><Button className="btn-yellow" onClick={saveEditLink} data-testid="edit-link-save">Spremi</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </main>
    </div>
  );
}
